import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TelegramConnection } from "@/components/TelegramConnection";
import { Settings as SettingsIcon, MessageCircle, Bell, User, TrendingUp, Copy, CheckCircle, XCircle } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const Settings = () => {
  const { toast } = useToast();
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  const webhookUrl = "https://cristos-ai.lovable.app/api/tv-alerts-webhook?token=1f7a3d2b-2c3f-4b6e-9a2a-6c1c9f4c8a7e";
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
    toast({
      title: "Copiado!",
      description: "URL copiado para área de transferência",
    });
  };

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="space-y-3 fade-in-up">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-4xl font-bold tracking-tight text-gradient">
            Configurações
          </h1>
          <Badge className="bg-primary/10 text-primary border-primary/20">
            <SettingsIcon className="h-3 w-3 mr-1" />
            Preferências
          </Badge>
        </div>
        <p className="text-muted-foreground text-lg max-w-2xl">
          Gerencie suas preferências, integrações e notificações
        </p>
        <div className="gradient-line w-24" />
      </div>

      <div className="fade-in-up delay-100">
        <Tabs defaultValue="integrations" className="w-full">
          <TabsList className="grid w-full grid-cols-3 glass-card">
            <TabsTrigger value="integrations" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Integrações
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notificações
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Perfil
            </TabsTrigger>
          </TabsList>

          <TabsContent value="integrations" className="space-y-6 mt-6">
            <Card className="glass-card hover-scale">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span>Telegram Integration</span>
                    <p className="text-sm text-muted-foreground font-normal mt-0.5">
                      Receba relatórios e alertas automáticos
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TelegramConnection />
              </CardContent>
            </Card>

            <Card className="glass-card hover-scale">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <span>TradingView Webhook</span>
                    <p className="text-sm text-muted-foreground font-normal mt-0.5">
                      Configure alertas do TradingView
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Webhook URL</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 px-3 py-2 text-xs bg-muted/50 rounded-lg border border-border font-mono overflow-x-auto">
                      {webhookUrl}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(webhookUrl)}
                      className="flex items-center gap-1 shrink-0"
                    >
                      {copiedUrl ? (
                        <>
                          <CheckCircle className="h-3 w-3 text-success" />
                          Copiado
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" />
                          Copiar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge className="bg-success/10 text-success border-success/20 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Ativo
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Configure este URL no TradingView
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card hover-scale">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-success/10 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <span>Status do Sistema</span>
                    <p className="text-sm text-muted-foreground font-normal mt-0.5">
                      Monitoramento dos serviços integrados
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <span className="text-sm font-medium">TradingView Webhook</span>
                  <Badge className="bg-success/10 text-success border-success/20 flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
                    Online
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <span className="text-sm font-medium">Telegram Bot</span>
                  <Badge className="bg-success/10 text-success border-success/20 flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
                    Online
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <span className="text-sm font-medium">Database</span>
                  <Badge className="bg-success/10 text-success border-success/20 flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
                    Online
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6 mt-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Bell className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span>Configurações de Notificações</span>
                    <p className="text-sm text-muted-foreground font-normal mt-0.5">
                      Configure quando e como receber notificações
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-8">
                  <Badge variant="outline" className="text-muted-foreground">
                    Em desenvolvimento...
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile" className="space-y-6 mt-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span>Configurações do Perfil</span>
                    <p className="text-sm text-muted-foreground font-normal mt-0.5">
                      Gerencie suas informações pessoais
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-8">
                  <Badge variant="outline" className="text-muted-foreground">
                    Em desenvolvimento...
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
